import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import {
  ApiPolicy,
  ApiPolicyAction,
  ApiPolicyControl,
  ApiPolicyDetail,
  ApiPolicyStep,
  ApiSinisterListOption,
} from '../../../../../core/models/myspace-api.models';
import { MyspaceApiService } from '../../../../../core/services/myspace-api.service';
import { extractApiErrorMessage } from '../../../../../core/utils/api-error.util';
import { extractPdfBase64, openPdfFromBase64 } from '../../../../../core/utils/pdf.util';

export interface PolicyClaimConcern {
  label: string;
  first: string;
  second: string;
}

const SINISTER_DOUBLE_FIELDS = new Set([
  'amount',
  'amount_capi_advance_acquired_value',
  'amount_capi_advance_needed_value',
  'amount_capi_advance_available_value',
  'amount_capi_partial_acquired_value',
  'amount_capi_partial_needed_value',
  'amount_capi_partial_available_value',
  'amount_capi_total_acquired_value',
  'amount_capi_total_needed_value',
  'amount_capi_committed_term_acquired_value',
  'amount_capi_committed_term_needed_value',
]);

@Component({
  selector: 'app-policy-claim-form',
  templateUrl: './policy-claim-form.component.html',
  styleUrls: ['./policy-claim-form.component.scss'],
})
export class PolicyClaimFormComponent implements OnInit, OnDestroy {
  @Input() action: ApiPolicyAction | null = null;
  @Input() policy: ApiPolicy | null = null;
  @Input() policyDetail: ApiPolicyDetail | null = null;
  @Input() concern: PolicyClaimConcern | null = null;
  @Input() policyNumber = '';
  @Input() typeCode = '';
  @Input() userId = '';
  @Input() currency = 'FCFA';

  @Output() back = new EventEmitter<void>();
  @Output() completed = new EventEmitter<void>();
  @Output() failed = new EventEmitter<{ message: string; retry: () => void }>();

  form = this.fb.group({});
  steps: ApiPolicyStep[] = [];
  currentPage = 0;
  showRecap = false;
  submitting = false;
  success = false;
  successReference = '';
  locationEnabled = false;
  photos: Array<File | null> = [];
  photoPreviews: Array<string | null> = [];
  downloadingReceipt = false;
  private photoStepVisited = false;

  constructor(
    private readonly fb: FormBuilder,
    private readonly myspaceApi: MyspaceApiService,
  ) {}

  get isClaimAction(): boolean {
    return String(this.action?.name || '').split('-')[0] === 'claim';
  }

  get currentStep(): ApiPolicyStep | null {
    return this.steps[this.currentPage] || null;
  }

  get currentControls(): ApiPolicyControl[] {
    return [...(this.currentStep?.controls || [])].sort((a, b) => Number(a.ordre || 0) - Number(b.ordre || 0));
  }

  get isPhotoStep(): boolean {
    return (this.currentStep?.type || '') === 'screenShot';
  }

  get isFirst(): boolean {
    return this.currentPage === 0;
  }

  get isLast(): boolean {
    return !this.steps.length || this.currentPage >= this.steps.length - 1;
  }

  get stepTitle(): string {
    const label = String(this.currentStep?.label || '').trim();
    if (!label) {
      return `Étape ${this.currentPage + 1}`;
    }
    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  get progress(): number {
    if (!this.steps.length) {
      return 0;
    }
    return (this.currentPage + 1) / this.steps.length;
  }

  get coverageFrom(): string {
    return String(
      this.policyDetail?.quittanceDetail?.dateeffe ||
        this.policyDetail?.quittanceDetail?.DATEEFFE ||
        this.policy?.DATEEFFE ||
        '-',
    );
  }

  get coverageTo(): string {
    return String(
      this.policyDetail?.quittanceDetail?.dateeche ||
        this.policyDetail?.quittanceDetail?.DATEECHE ||
        this.policy?.DATEECHE ||
        '-',
    );
  }

  get coverageTitle(): string {
    return this.policyDetail?.sinisterDetail?.secondDetailTitle?.trim() || 'Couverture';
  }

  get recapControls(): ApiPolicyControl[] {
    return this.steps
      .flatMap((step) => step.controls || [])
      .filter((control) => String(control.label || '').trim() !== 'signaler ma position');
  }

  get successControls(): ApiPolicyControl[] {
    const reference: ApiPolicyControl = {
      name: 'reference',
      label: `Numéro ${this.policyDetail?.sinisterDetail?.claimNammings || 'Opération'}`,
      type: 'text',
      showToSuccess: true,
    };
    return [reference, ...this.recapControls.filter((control) => control.showToSuccess)];
  }

  get photoCount(): number {
    return Number(this.action?.screenShotsNumber || 0);
  }

  get photoMinRequired(): number {
    return Number(this.action?.screenShotMinRequired || 0);
  }

  get successMessage(): string {
    return this.policyDetail?.sinisterDetail?.successMessage?.trim() || 'Opération réussie';
  }

  ngOnInit(): void {
    this.steps = [...(this.action?.steps || [])].sort((a, b) => Number(a.ordre || 0) - Number(b.ordre || 0));
    const group: Record<string, FormControl> = {};
    this.steps.forEach((step) => {
      (step.controls || []).forEach((control) => {
        const name = String(control.name || '');
        if (!name || group[name]) {
          return;
        }
        const initial = this.initialDisplayValue(control);
        group[name] = this.fb.control(
          { value: initial, disabled: control.enabled === false },
          { validators: this.validatorsFor(control) },
        );
      });
    });
    this.form = this.fb.group(group);
    this.photos = Array.from({ length: this.photoCount }, () => null);
    this.photoPreviews = Array.from({ length: this.photoCount }, () => null);
  }

  ngOnDestroy(): void {
    this.photoPreviews.forEach((url) => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    });
  }

  controlType(control: ApiPolicyControl): string {
    return String(control.type || 'text').toLowerCase();
  }

  fieldControl(name: string | null | undefined): FormControl {
    return (this.form.get(String(name || '')) as FormControl) || this.fb.control('');
  }

  sentence(value: string | null | undefined): string {
    const text = String(value || '').trim();
    if (!text) {
      return '';
    }
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  listOptions(control: ApiPolicyControl): ApiSinisterListOption[] {
    const key = String(control.requiredDataListType || '');
    const dictionary = this.policyDetail?.sinisterDetail?.dataListDictionnary || {};
    return dictionary[key] || [];
  }

  listOptionLabel(option: ApiSinisterListOption): string {
    return String(option['label'] ?? option['code'] ?? '');
  }

  listOptionValue(option: ApiSinisterListOption): string {
    return String(option['code'] ?? option['label'] ?? '');
  }

  dateInputValue(name: string): string {
    return this.toIsoDate(String(this.form.get(name)?.value ?? ''));
  }

  displayValue(control: ApiPolicyControl): string {
    if (control.name === 'reference') {
      return this.successReference || '-';
    }
    const raw = String(this.form.get(String(control.name || ''))?.value ?? '').trim();
    if (!raw) {
      return 'Non renseigné';
    }
    if (this.controlType(control) === 'list') {
      const option = this.listOptions(control).find((item) => this.listOptionValue(item) === raw);
      return option ? this.listOptionLabel(option) : raw;
    }
    const formatted = this.formatNumberDisplay(raw);
    const suffix = control.format === 'currency' ? this.currency : control.format === 'percent' ? '%' : '';
    return `${formatted} ${suffix}`.trim();
  }

  isLastSuccessItem(index: number): boolean {
    return index === this.successControls.length - 1;
  }

  onCurrencyInput(name: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    const formatted = this.formatWithSpace(input.value);
    this.form.get(name)?.setValue(formatted, { emitEvent: false });
    input.value = formatted;
  }

  onDateInput(name: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    this.form.get(name)?.setValue(this.toOrsysDate(input.value));
  }

  async onToggleLocation(name: string, enabled: boolean): Promise<void> {
    this.locationEnabled = enabled;
    if (!enabled) {
      this.form.get(name)?.setValue(null);
      return;
    }
    try {
      const position = await this.readPosition();
      this.form.get(name)?.setValue(JSON.stringify(position));
    } catch {
      this.locationEnabled = false;
      this.form.get(name)?.setValue(null);
    }
  }

  onPhotoSelected(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;
    this.photos[index] = file;
    if (this.photoPreviews[index]) {
      URL.revokeObjectURL(this.photoPreviews[index] as string);
    }
    this.photoPreviews[index] = file ? URL.createObjectURL(file) : null;
  }

  nextStep(): void {
    if (this.isPhotoStep) {
      this.photoStepVisited = true;
      const count = this.photos.filter(Boolean).length;
      if (this.isLast && count < this.photoMinRequired) {
        this.failed.emit({
          message: `${this.action?.screenShotValidMsg || ''} ${this.photoMinRequired}`.trim(),
          retry: () => this.nextStep(),
        });
        return;
      }
    } else if (!this.validateCurrentStep()) {
      this.currentControls.forEach((control) => this.form.get(String(control.name || ''))?.markAsTouched());
      return;
    }

    if (!this.isLast) {
      this.currentPage += 1;
      return;
    }

    if (this.photoStepVisited) {
      const count = this.photos.filter(Boolean).length;
      if (count < this.photoMinRequired) {
        this.failed.emit({
          message: `${this.action?.screenShotValidMsg || ''} ${this.photoMinRequired}`.trim(),
          retry: () => this.nextStep(),
        });
        return;
      }
    }

    this.showRecap = true;
  }

  previousStep(): void {
    if (this.showRecap) {
      this.showRecap = false;
      return;
    }
    if (this.currentPage > 0) {
      this.currentPage -= 1;
      return;
    }
    this.back.emit();
  }

  async submit(): Promise<void> {
    if (this.submitting) {
      return;
    }
    this.submitting = true;
    try {
      const payload = this.buildPayload();
      const files = this.photos.filter((file): file is File => !!file);
      const response = await firstValueFrom(this.myspaceApi.createSinister(payload, files));
      this.successReference = String(response?.['reference'] ?? '-');
      this.success = true;
      this.showRecap = false;
    } catch (err) {
      this.failed.emit({
        message: extractApiErrorMessage(err, 'Impossible de déclarer la prestation.'),
        retry: () => {
          void this.submit();
        },
      });
    } finally {
      this.submitting = false;
    }
  }

  finish(): void {
    this.completed.emit();
  }

  async downloadReceipt(): Promise<void> {
    if (this.downloadingReceipt || !this.successReference || this.successReference === '-') {
      return;
    }
    this.downloadingReceipt = true;
    try {
      const raw = await firstValueFrom(
        this.myspaceApi.downloadSinisterReceipt(this.userId, this.policyNumber, this.successReference),
      );
      openPdfFromBase64(extractPdfBase64(raw), `at_${this.successReference}.pdf`);
    } catch (err) {
      this.failed.emit({
        message: extractApiErrorMessage(err, 'Impossible de télécharger l’attestation.'),
        retry: () => {
          void this.downloadReceipt();
        },
      });
    } finally {
      this.downloadingReceipt = false;
    }
  }

  inputChecked(event: Event): boolean {
    return (event.target as HTMLInputElement | null)?.checked ?? false;
  }

  private validateCurrentStep(): boolean {
    return this.currentControls.every((control) => {
      const field = this.form.get(String(control.name || ''));
      field?.updateValueAndValidity();
      return !field || field.disabled || field.valid;
    });
  }

  private validatorsFor(control: ApiPolicyControl): ValidatorFn[] {
    const validators: ValidatorFn[] = [];
    if (!control.required) {
      return validators;
    }
    validators.push(Validators.required);
    if (control.minimum != null || control.maximum != null) {
      validators.push(this.numericRangeValidator(control));
    }
    return validators;
  }

  private numericRangeValidator(control: ApiPolicyControl): ValidatorFn {
    return (field: AbstractControl): ValidationErrors | null => {
      const raw = String(field.value ?? '').trim();
      if (!raw) {
        return { required: true };
      }
      const parsed = this.parseDouble(raw);
      if (parsed == null) {
        return { numeric: true };
      }
      if (control.minimum != null && parsed < Number(control.minimum)) {
        return { min: { min: control.minimum } };
      }
      if (control.maximum != null && parsed > Number(control.maximum)) {
        return { max: { max: control.maximum } };
      }
      return null;
    };
  }

  private buildPayload(): Record<string, string> {
    const payload: Record<string, string> = {
      policeNumber: this.policyNumber,
      typeCode: this.typeCode,
      userId: this.userId,
      status: '0',
      actionName: String(this.action?.name || ''),
    };

    Object.entries(this.form.getRawValue()).forEach(([key, value]) => {
      if (value == null || String(value).trim() === '') {
        return;
      }
      const raw = String(value).trim();
      if (this.controlTypeFromName(key) === 'date') {
        payload[key] = this.toOrsysDate(raw);
        return;
      }
      if (SINISTER_DOUBLE_FIELDS.has(key) || this.parseDouble(raw) != null) {
        const parsed = this.parseDouble(raw);
        if (parsed != null) {
          payload[key] = String(parsed);
          return;
        }
        if (SINISTER_DOUBLE_FIELDS.has(key)) {
          return;
        }
      }
      payload[key] = raw;
    });

    return payload;
  }

  private controlTypeFromName(name: string): string {
    for (const step of this.steps) {
      const match = (step.controls || []).find((control) => String(control.name || '') === name);
      if (match) {
        return this.controlType(match);
      }
    }
    return 'text';
  }

  private initialDisplayValue(control: ApiPolicyControl): string {
    const raw = control.initialValue;
    if (raw == null || String(raw).trim() === '') {
      return '';
    }
    if (control.format === 'currency' || SINISTER_DOUBLE_FIELDS.has(String(control.name || ''))) {
      return this.formatWithSpace(String(raw));
    }
    return String(raw);
  }

  private parseDouble(value: string): number | null {
    const normalized = value.replace(/\s+/g, '').replace(',', '.');
    if (!normalized || /[a-zA-Z]/.test(normalized)) {
      return null;
    }
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private formatWithSpace(value: string): string {
    const clean = value.replace(/[^\d.,]/g, '').replace(',', '.');
    if (!clean) {
      return '';
    }
    const [intPart, ...rest] = clean.split('.');
    const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return rest.length ? `${grouped}.${rest.join('')}` : grouped;
  }

  private formatNumberDisplay(value: string): string {
    const parsed = this.parseDouble(value);
    if (parsed == null) {
      return this.toOrsysDate(value) || value;
    }
    return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(parsed);
  }

  private toOrsysDate(value: string): string {
    const iso = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) {
      return `${iso[3]}/${iso[2]}/${iso[1]}`;
    }
    return value;
  }

  private toIsoDate(value: string): string {
    const orsys = value.trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (orsys) {
      return `${orsys[3]}-${orsys[2]}-${orsys[1]}`;
    }
    return /^\d{4}-\d{2}-\d{2}$/.test(value.trim()) ? value.trim() : '';
  }

  private readPosition(): Promise<Record<string, number | string | null>> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('unsupported'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          resolve({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
            altitude: pos.coords.altitude,
            heading: pos.coords.heading,
            speed: pos.coords.speed,
            speed_accuracy: (pos.coords as GeolocationCoordinates & { speedAccuracy?: number }).speedAccuracy ?? null,
            timestamp: new Date(pos.timestamp).toISOString(),
          });
        },
        () => reject(new Error('denied')),
        { enableHighAccuracy: true, timeout: 8000 },
      );
    });
  }
}
